<template>
  <div class="content">
    <h1 class="title">{{ $t("anomaly.title") }}</h1>
    <div class="table">
      <div class="inputs">
        <v-row>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <div class="flatpickr">
              <label
                for="date"
                :class="
                  labelForFlatpickr ? 'flatpickrLabel' : 'flatpickrHiddenLabel'
                "
                >{{ $t("anomaly.datePlaceholderFrom") }}</label
              >
              <div class="date-picker">
                <flat-pickr
                  v-model="date"
                  :config="config"
                  name="date"
                ></flat-pickr>
              </div>
            </div>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-select
              :label="$t('anomaly.anomalyTypePlaceholder')"
              :items="anomalyTypes"
              v-model="anomalyType"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-select
              :label="$t('anomaly.providerPlaceholder')"
              v-model="provider"
              :items="providerTypes"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
        </v-row>
        <v-btn @click="handleFilter(false)">{{
          $t("anomaly.filterButton")
        }}</v-btn>
      </div>
      <div>
        <v-data-table
          v-if="!isLoading"
          :headers="headers"
          :items="anomalyEvents"
          disable-sort
          hide-default-footer
          :items-per-page="-1"
          class="elevation-0"
        >
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDateTime(item.selectable.created_at) }}
          </template>
          <template v-slot:[`item.provider`]="{ item }">
            {{ $t(`anomaly.provider.${item.selectable.provider}`) }}
          </template>
          <template v-slot:[`item.anomaly_type`]="{ item }">
            {{ $t(`anomaly.anomaly_type.${item.selectable.anomaly_type}`) }}
          </template>
          <template v-slot:no-data>
            <Vue3Lottie
              :animationData="NoDataLottie"
              :height="200"
              :width="200"
            />
            <div class="pb-5 table-no-data">
              <h3>{{ $t("tableNoDataTitle") }}</h3>
              <p>{{ $t("tableNoDataDesc") }}</p>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="botSide">
              <v-row
                align="center"
                justify-sm="end"
                justify="center"
                class="px-2"
              >
                <v-col cols="12" xs="12" lg="3" md="4" sm="6">
                  <v-row align="center" justify="end"
                    ><div class="pr-2 pageSelectorLabel">
                      {{ $t("anomaly.tableFooterText") }}
                    </div>
                    <v-select
                      v-model="limit"
                      hide-details
                      @update:model-value="handleFilter(false)"
                      :items="pageSizes"
                      class="pageSelector pl-5"
                    ></v-select>
                    <div class="pl-2">
                      {{ displayRange }}
                    </div>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  xs="12"
                  lg="3"
                  md="4"
                  sm="5"
                  class="pl-1 pr-1 pagination"
                >
                  <v-pagination
                    v-model="page"
                    :length="pageCount"
                    rounded="circle"
                    :total-visible="1"
                    show-first-last-page="true"
                    @update:model-value="handleFilter(true)"
                  ></v-pagination>
                </v-col>
              </v-row>
            </div>
          </template>
        </v-data-table>
      </div>
      <v-card v-if="isLoading" class="loading">
        <v-progress-circular
          v-if="isLoading"
          size="73.5"
          indeterminate
          class="loading-circle"
        ></v-progress-circular>
      </v-card>
    </div>
  </div>
</template>

<script>
import { Vue3Lottie } from "vue3-lottie";
import NoDataLottie from "@/assets/lotties/nodatalottie.json";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import moment from "moment";
import { Hungarian } from "flatpickr/dist/l10n/hu.js";
import { INVOICE_PROVIDER, PAYMENT_PROVIDER } from "../../enums";
import { PaymentProvider } from "@/types/PaymentProvider";

export default {
  name: "Anomaly",
  props: {},
  components: {
    flatPickr,
    Vue3Lottie,
  },
  data() {
    return {
      NoDataLottie,
      headers: [
        {
          title: this.$t("anomaly.table.headers.createdAt"),
          key: "created_at",
          align: "start",
          sortable: false,
        },
        {
          title: this.$t("anomaly.table.headers.provider"),
          key: "provider",
          sortable: false,
        },
        {
          title: this.$t("anomaly.table.headers.anomalyType"),
          key: "anomaly_type",
          sortable: false,
        },
        {
          title: this.$t("anomaly.table.headers.comment"),
          key: "comment",
          sortable: false,
        },
      ],

      hiddenLabel: false,

      //Pagination settings
      page: undefined,
      limit: 5,
      total: 0,
      pageSizes: [10, 25, 50, 100],

      //For Search
      anomalyType: undefined,
      provider: undefined,
      date: "",
      anomalyTypes: [
        this.$t("anomaly.anomaly_type.1"),
        this.$t("anomaly.anomaly_type.2"),
        this.$t("anomaly.anomaly_type.3"),
        this.$t("anomaly.anomaly_type.4"),
      ],
      providerTypes: [
        this.$t(`anomaly.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`),
        this.$t(`anomaly.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`),
        this.$t(`anomaly.invoice_providers.${INVOICE_PROVIDER.SZAMLAZZ_HU}`),
      ],

      anomalyEvents: [],

      // For Flatpicker
      config: {
        altFormat: "Y-m-d",
        altInput: true,
        dateFormat: "Y-m-d",
        allowInput: false,
        clear: true,
        mode: "range",
        disableMobile: true,
        enableTime: false,
        locale: {
          ...Hungarian,
          rangeSeparator: " - ",
        },
        onOpen: () => {
          this.hiddenLabel = true;
        },
        onClose: () => {
          this.hiddenLabel = false;
        },
        onKeyDown: (selectedDates, dateStr, instance, event) => {
          if (event.key === "Enter") {
            instance.close();
            this.handleFilter(false);
          }
        },
      },

      isLoading: false,
      error: false,

      requestBody: {},

      enterClicked: false,
    };
  },
  created() {
    this.getAnomaly();
  },

  methods: {
    ...mapActions({ getAnomalyEvents: "anomaly/getAnomalyEvents" }),
    getAnomaly(body) {
      this.isLoading = true;
      this.getAnomalyEvents(body)
        .then((res) => {
          this.anomalyEvents = res.events;
          this.page = res.page;
          this.limit = res.limit;
          this.total = res.total;
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
          this.isLoading = false;
          this.error = true;
        });
    },
    formatDate(dateString) {
      return moment(dateString).format("ll");
    },
    formatDateTime(dateString) {
      return moment(dateString).format("lll");
    },
    handleFilter(hasPage, keydown) {
      if (keydown) {
        this.enterClicked = true;
      }
      const date_from = this.date !== null ? this.date.split(" -") : "";
      const date_to = this.date !== null ? this.date.split("- ") : "";
      const requestBody = {
        date_from: date_from[0] !== undefined ? String(date_from[0]) : "",
        date_to: date_to[1] !== undefined ? String(date_to[1]) : "",
        type: this.anomalyTypeForSending(this.anomalyType),
        provider: this.providerForSending(this.provider),
        limit: this.limit,
        page: hasPage ? this.page : 1,
      };
      this.getAnomaly(requestBody);
      setTimeout(() => {
        this.enterClicked = false;
      }, 100);
    },
    anomalyTypeForSending(value) {
      const anomalyTypes = {
        [this.$t("anomaly.anomaly_type.1")]: 1,
        [this.$t("anomaly.anomaly_type.2")]: 2,
        [this.$t("anomaly.anomaly_type.3")]: 3,
        [this.$t("anomaly.anomaly_type.4")]: 4,
      };

      return anomalyTypes[value] || "";
    },
    providerForSending(value) {
      const providers = {
        [this.$t(`anomaly.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`)]:
          PaymentProvider.Simple,
        [this.$t(`anomaly.invoice_providers.${INVOICE_PROVIDER.SZAMLAZZ_HU}`)]:
          PaymentProvider.Szamlazz,
        [this.$t(`anomaly.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`)]:
          PaymentProvider.Szep,
      };

      return providers[value] || "";
    },
  },
  computed: {
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    displayRange() {
      const start = (this.page - 1) * this.limit + 1;
      const end = Math.min(start + this.limit - 1, this.total);
      return `${start}-${end} of ${this.total}`;
    },
    labelForFlatpickr() {
      return !this.hiddenLabel && (this.date === "" || this.date === null);
    },
  },
};
</script>

<style lang="scss" scoped></style>
